import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, PanelLeft, Search } from "lucide-react";
import { TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { endpoints } from "@/lib/api/endpoints";
import type { Category, MenuItem, TableGridArea } from "@/lib/api/types";
import { PERMISSIONS, hasPermission, useSession } from "@/modules/auth/useSession";
import { useFloorKitchenTab } from "@/modules/kitchen/components/PaperKitchenView";
import { canUseBackOffice, holdsAdministrationPermission } from "@/modules/auth/backOffice";
import { resolveUnitChargedPrice } from "@/modules/pos/lib/pricing";
import { useLocalName } from "@/shared/localName";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useMoney } from "@/shared/ui";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { TillPhoneControls } from "@/shared/ui";
import { busyCount } from "../lib/floorTile";
import { TILL_RETURN_PARAM } from "../lib/tillReturn";
import { useDeliveryTabBadge } from "../lib/useDeliveryTabBadge";
import { FLOOR_TAB_ICONS, PaperFloorTabs } from "./PaperFloor";
import { PaperBackTile, PaperDishFace, PaperSectionTile } from "./PaperMenu";

/**
 * THE PAPER REGISTER (founder, 2026-10-03: "there's a new flow for the POS").
 *
 * The redesign's `pos` register (`Restaurant Dashboard.dc.html`, the register
 * template and `posVals`) in two steps instead of a chip row over one grid:
 *
 *  1. SECTIONS — a tile per menu section (its badge on its tinted ground, how
 *     many dishes it holds, the blue ring and count when the bill has any),
 *     led by a grey "Back · to Tables".
 *  2. DISHES — the chosen section's dish tiles, led by "Back · to Categories".
 *
 * Typing in the search lists matching dishes from EVERY section, led by
 * "Back · Clear search". The crumbs over it read "Tables / <place> /
 * <section>". The section lives in the URL (`?section=`) so a reload keeps the
 * step; it is written with `replace`, so the browser's Back still leaves the
 * screen rather than stepping through sections mid-service.
 *
 * Rendered by `MenuBoard` while PAPER is the look on a light till. Behaviour is
 * the board's: the same `onAdd`, the same disabled and sold-out rules, the
 * same tier pricing. The tile's old controls (price override, swap, 86) are
 * not drawn: a tap adds for every role, and those acts open from the tile's
 * press-and-hold sheet (and inside the add-on picker); a sold-out tile is
 * itself the way back for a session that may 86.
 */
export const SECTION_PARAM = "section";
/** The `?section=` of the quiet "Other dishes" tile — dishes filed nowhere. */
export const OTHER_SECTION = "other";

export function PaperRegisterBoard({
  menu,
  categories,
  countsByProductId,
  disabled,
  onAdd,
  discountPercent,
  fromPrice,
  notice,
  narrow,
  head,
  tablesHref,
  backLabel,
  crumbs,
  onToggleAvailability,
  togglingProductId = null,
  onOverride,
  onSubstitute,
  phoneLead,
}: {
  menu: MenuItem[];
  categories: Category[];
  countsByProductId: Map<string, number>;
  disabled: boolean;
  onAdd: (item: MenuItem) => void;
  discountPercent: number;
  /**
   * The lowest price this dish can be added at, when it asks for a size or
   * extras first — the tile then reads "from" it. `null` for a dish added in
   * one tap, which shows its price.
   */
  fromPrice?: (item: MenuItem) => number | null;
  notice?: ReactNode;
  narrow?: "show" | "hide";
  /** The Tables / Kitchen tabs over the register. */
  head?: ReactNode;
  /** Where "Tables" and "Back · to Tables" go. */
  tablesHref: string;
  /**
   * B6 · P24b — the Back tile's second line on the first step; "to Tables"
   * unless given (a bill at no table goes back to Delivery & takeaway).
   */
  backLabel?: string;
  /** B6 · P24b — the crumbs on a bill at no table: the root and the place. */
  crumbs?: { root: string; place: string };
  /**
   * 86 (§5.8, D32) — for `MENU_MANAGE` ∥ `MENU_AVAILABILITY`, as before. Here
   * it puts a SOLD-OUT dish back: its tile stays tappable for that session and
   * asks first (the shared confirm). Marking one out is in the add-on picker.
   */
  onToggleAvailability?: (item: MenuItem) => void;
  togglingProductId?: string | null;
  /** "Different price" (D46) — `PRICE_OVERRIDE` only; on the hold sheet. */
  onOverride?: (item: MenuItem) => void;
  /** "Swap out" (D61) — `INVOICE_SUBSTITUTE` only, never a combo; on the hold sheet. */
  onSubstitute?: (item: MenuItem) => void;
  /**
   * Waiter phone (2026-10-28): the table and its bill, at the START of the
   * phone's one head line — before the search, with the bell and sign-out at
   * its end. Below `lg` the page's top bar is not shown (`Screen` `phoneHead`).
   */
  phoneLead?: ReactNode;
}) {
  const t = useTranslate();
  const localName = useLocalName();
  const money = useMoney();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const active = menu.filter((item) => item.isActive);
  // Only sections that hold a dish — an empty tile on a till is a dead tap.
  const sections = categories.filter((category) =>
    active.some((item) => item.categoryIds.includes(category.id)),
  );
  // An id that no longer names a section (deleted, emptied) is the first step.
  const section =
    sections.find((category) => category.id === params.get(SECTION_PARAM)) ?? null;
  /*
   * Dishes in NO section this board lists — unfiled, or filed only under a
   * hidden section (audit, 2026-10-03). The old "All" chip reached them; here
   * a quiet "Other dishes" tile does, last, and only when there are any.
   */
  const unfiled = active.filter(
    (item) => !item.categoryIds.some((id) => sections.some((category) => category.id === id)),
  );
  const other =
    section === null && unfiled.length > 0 && params.get(SECTION_PARAM) === OTHER_SECTION;
  const otherSection = {
    id: OTHER_SECTION,
    nameAr: t((m) => m.paperRegister.otherDishes),
    nameEn: t((m) => m.paperRegister.otherDishes),
    badge: "cutlery",
  } as Category;
  const needle = query.trim().toLowerCase();
  const searching = needle.length > 0;

  const setSection = (id: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set(SECTION_PARAM, id);
        else next.delete(SECTION_PARAM);
        return next;
      },
      { replace: true },
    );
  const toTables = () => navigate(tablesHref);

  const dishes = searching
    ? active.filter(
        (item) =>
          /* BOTH names, whatever the screen's language: a cashier typing
             "fries" must find it on an Arabic till, and "بطاطس" on an English
             one. Display picks a language; search must not. */
          item.nameAr.toLowerCase().includes(needle) ||
          (item.nameEn ?? "").toLowerCase().includes(needle),
      )
    : section
      ? active.filter((item) => item.categoryIds.includes(section.id))
      : other
        ? unfiled
        : null;

  const plates = (items: readonly MenuItem[]) =>
    items.reduce((sum, item) => sum + (countsByProductId.get(item.id) ?? 0), 0);

  /*
   * THE PHONE'S MENU IS ONE SCROLL (waiter phone, 2026-10-28: "the filter is
   * sorting the breakfast first but you can see the rest of the menu under
   * it"). Below `lg` a chosen section opens the WHOLE menu at that section —
   * every section under its own heading, in menu order — instead of that
   * section alone. A sticky chip row leads it: Back (to the sections, the
   * founder's "important" button, now a chip instead of a dish tile), then a
   * chip per section that jumps there and lights up while its section is on
   * screen. The sections step and search are as before, Back a chip too.
   * A desk keeps the two steps and the Back tile: it has the width for both.
   */
  const phone = useIsMobile();
  const currentId = section?.id ?? (other ? OTHER_SECTION : null);
  const flow = phone && !searching && currentId !== null;
  const groups = [
    ...sections.map((category) => ({
      id: category.id,
      category,
      items: active.filter((item) => item.categoryIds.includes(category.id)),
    })),
    ...(unfiled.length > 0
      ? [{ id: OTHER_SECTION, category: otherSection, items: unfiled }]
      : []),
  ];
  /* Found through the DOM by id, not refs: only scroll handlers read them. */
  const rootId = useId();
  const rootEl = () => document.getElementById(rootId);
  const chipsEl = () => rootEl()?.querySelector<HTMLElement>('[data-ui="till-menu-pin"]');
  const headEl = (id: string) =>
    rootEl()?.querySelector<HTMLElement>(`[data-flow-head="${CSS.escape(id)}"]`);
  /** The section on screen while scrolling; `null` = the chosen one. */
  const [spy, setSpy] = useState<string | null>(null);
  const lit = spy ?? currentId;
  const scrollToSection = (id: string) => {
    const root = rootEl();
    const head = headEl(id);
    if (!root || !head) return;
    const offset = (chipsEl()?.offsetHeight ?? 0) + 4;
    root.scrollTo?.({
      top:
        head.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - offset,
    });
  };
  // Opening a section (from its tile, or a chip naming another) lands on it.
  useEffect(() => {
    if (flow && currentId) scrollToSection(currentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on the section, not on every render
  }, [flow, currentId]);
  // The lit chip stays in view in its own row.
  useEffect(() => {
    if (!lit) return;
    rootEl()
      ?.querySelector<HTMLElement>(`[data-flow-chip="${CSS.escape(lit)}"]`)
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on the lit chip only
  }, [lit]);
  const onFlowScroll = () => {
    const root = rootEl();
    if (!flow || !root) return;
    const line = root.getBoundingClientRect().top + (chipsEl()?.offsetHeight ?? 0) + 8;
    let onScreen: string | null = null;
    for (const group of groups) {
      const head = headEl(group.id);
      if (head && head.getBoundingClientRect().top <= line) onScreen = group.id;
    }
    const next = onScreen ?? groups[0]?.id ?? null;
    if (next !== lit) setSpy(next);
  };
  const pickChip = (id: string) => {
    setSpy(null);
    if (id === currentId) scrollToSection(id);
    else setSection(id);
  };
  const back = searching
    ? { label: t((m) => m.paperRegister.clearSearch), run: () => setQuery(""), testId: "register-back-clear" }
    : currentId !== null
      ? {
          label: `${t((m) => m.paperRegister.back)} ${t((m) => m.paperRegister.backToSections)}`,
          run: () => {
            setSpy(null);
            setSection(null);
          },
          testId: "register-back-sections",
        }
      : {
          label: `${t((m) => m.paperRegister.back)} ${backLabel ?? t((m) => m.paperRegister.backToTables)}`,
          run: toTables,
          testId: "register-back-tables",
        };

  /*
   * THE DISH'S OTHER ACTS (founder, 2026-10-03: "do what you recommend").
   * A tap ADDS, for every role. Press-and-hold (≈450ms, cancelled by a move),
   * a right-click, or the ContextMenu key / Shift+F10 on a focused tile opens
   * a small sheet with the acts this session may do on this dish — each the
   * same dialog or confirm the tile's old controls opened. None held: no sheet.
   */
  const [sheet, setSheet] = useState<{ item: MenuItem; tile: HTMLElement } | null>(null);
  const holdHintId = useId();
  const actionsOf = (item: MenuItem): DishAction[] => {
    const soldOut = !item.isAvailable;
    const actions: DishAction[] = [];
    // A price or a swap only on a dish this bill can still take.
    if (!soldOut && !disabled && onOverride) {
      actions.push({
        key: "override",
        label: t((m) => m.pos.priceOverrideAction),
        run: () => onOverride(item),
      });
    }
    if (!soldOut && !disabled && onSubstitute && !item.isCombo) {
      actions.push({
        key: "swap",
        label: t((m) => m.pos.swapRemoveLabel),
        run: () => onSubstitute(item),
      });
    }
    // 86 does not depend on the bill (§5.8).
    if (onToggleAvailability) {
      actions.push({
        key: "availability",
        label: soldOut
          ? t((m) => m.pos.menuUnmarkSoldOutShort)
          : t((m) => m.pos.menuMarkSoldOutShort),
        run: () => onToggleAvailability(item),
      });
    }
    return actions;
  };

  const dishTile = (item: MenuItem) => {
    const inOrder = countsByProductId.get(item.id) ?? 0;
    // 86'd tonight (D32). Disabled, NOT removed: a waiter has to be able to
    // answer "do you have the fish?".
    const soldOut = !item.isAvailable;
    // A sold-out tile is live only for a session that may put it back.
    const canPutBack = soldOut && onToggleAvailability !== undefined;
    // B18 — an ingredient a PERSON has called finished: the dish still sells.
    const ingredientsOut = item.ingredientsMarkedOut ?? [];
    const from = fromPrice?.(item) ?? null;
    const price =
      from !== null
        ? t((m) => m.paperRegister.fromPrice, { amount: money(from) })
        : money(resolveUnitChargedPrice(item.price, discountPercent));
    const actions = actionsOf(item);
    const hint =
          canPutBack
            ? t((m) => m.pos.menuUnmarkSoldOut, { name: localName(item) })
            : soldOut
            ? item.unavailableBecause === "COMPONENT"
              ? t((m) => m.pos.comboSoldOutHint)
              : t((m) => m.pos.menuSoldOutHint)
            : ingredientsOut.length > 0
              ? t((m) => m.pos.menuIngredientOutHint, {
                  items: ingredientsOut.map((row) => row.name).join("، "),
                })
              : undefined;
    const holdHint = t((m) => m.paperRegister.holdHint);
    return (
      <PaperDishButton
        key={item.id}
        itemId={item.id}
        disabled={canPutBack ? togglingProductId === item.id : disabled || soldOut}
        ariaDisabled={soldOut && !canPutBack}
        title={actions.length > 0 ? (hint ? `${hint} · ${holdHint}` : holdHint) : hint}
        describedBy={actions.length > 0 ? holdHintId : undefined}
        soldOut={soldOut}
        inOrder={inOrder}
        onTap={() => (canPutBack ? onToggleAvailability(item) : onAdd(item))}
        onHold={actions.length > 0 ? (tile) => setSheet({ item, tile }) : undefined}
      >
        <PaperDishFace
          item={item}
          name={localName(item)}
          price={price}
          inOrder={inOrder}
          soldOut={soldOut}
          soldOutLabel={t((m) => m.paperRegister.soldOut)}
          ingredientOutLabel={
            ingredientsOut.length > 0 ? t((m) => m.pos.menuIngredientOut) : null
          }
          categories={categories}
          extras={
            // A combo's price is for the whole plate (S16, D52).
            item.isCombo ? (
              <span data-ui="dish-tile-mark" data-testid={`menu-item-combo-${item.id}`}>
                {t((m) => m.pos.comboBadge)}
              </span>
            ) : null
          }
        />
      </PaperDishButton>
    );
  };

  return (
    <div
      id={rootId}
      onScroll={flow ? onFlowScroll : undefined}
      data-ui="till-menu"
      data-flow={flow ? "" : undefined}
      data-step={searching ? "search" : section || other ? "dishes" : "sections"}
      className={[
        narrow === "hide" ? "hidden lg:flex" : "flex",
        "min-h-0 min-w-0 flex-1 flex-col overflow-y-auto",
      ].join(" ")}
    >
      {notice ? <div className="shrink-0">{notice}</div> : null}
      {/* The Tables / Kitchen tabs are the desk's; the phone's head is one line. */}
      {phone ? null : head}
      {crumbs ? (
        <PaperOffPremiseCrumbs
          root={crumbs.root}
          place={crumbs.place}
          section={!searching && (section || other) ? localName(section ?? otherSection) : null}
          onRoot={toTables}
          onPlace={() => setSection(null)}
        />
      ) : null}
      {/* No breadcrumb (founder, 2026-10-07): the Back tile leads out of a
          section and the floor's own toggle leads to the tables. */}
      {/* The phone pins its head line and the chip row together, over the
          one scroll (2026-10-28). */}
      {phone ? (
        <div data-ui="till-menu-pin">
          <div data-ui="till-menu-tools" className="flex shrink-0 items-center">
            {phone && phoneLead ? (
              <>
                {phoneLead}
                <span data-ui="till-phone-rule" aria-hidden="true" />
              </>
            ) : null}
            <div data-ui="till-menu-search" className="relative w-full">
              <Search
                aria-hidden="true"
                size={15}
                strokeWidth={1.9}
                data-ui="till-menu-search-icon"
                className="pointer-events-none absolute top-1/2 -translate-y-1/2"
              />
              <TextInput
                name="pos-menu-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t((m) => m.paperRegister.searchDishes)}
                aria-label={t((m) => m.paperRegister.searchDishes)}
                data-testid="menu-search"
              />
            </div>
            {phone ? (
              <>
                <span className="flex-1" />
                <TillPhoneControls />
              </>
            ) : null}
          </div>
          <div data-ui="register-chips" data-testid="register-chips">
            <button
              type="button"
              data-ui="register-back-chip"
              data-testid={back.testId}
              onClick={back.run}
            >
              <ArrowRight size={15} strokeWidth={2} aria-hidden="true" className="ltr:-scale-x-100" />
              {back.label}
            </button>
            {flow
              ? groups.map((group) => (
                  <button
                    key={group.id}
                    data-flow-chip={group.id}
                    type="button"
                    data-ui="register-chip"
                    aria-current={lit === group.id ? "true" : undefined}
                    data-testid={`register-chip-${group.id}`}
                    onClick={() => pickChip(group.id)}
                  >
                    {localName(group.category)}
                    <span data-ui="register-chip-count">{group.items.length}</span>
                  </button>
                ))
              : searching
                ? null
                : (
                  /* The sections step names itself, as the design's chip row does. */
                  <span data-ui="register-chip" aria-current="true" data-testid="register-chip-sections">
                    {t((m) => m.paperRegister.sectionsChip)}
                  </span>
                )}
          </div>
        </div>
      ) : (
        <div data-ui="till-menu-tools" className="flex shrink-0 items-center">
          <div data-ui="till-menu-search" className="relative w-full">
            <Search
              aria-hidden="true"
              size={15}
              strokeWidth={1.9}
              data-ui="till-menu-search-icon"
              className="pointer-events-none absolute top-1/2 -translate-y-1/2"
            />
            <TextInput
              name="pos-menu-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t((m) => m.paperRegister.searchDishes)}
              aria-label={t((m) => m.paperRegister.searchDishes)}
              data-testid="menu-search"
            />
          </div>
        </div>
      )}
      {flow ? (
        groups.map((group) => (
          <section key={group.id} data-ui="register-flow-section" data-testid={`register-flow-${group.id}`}>
            <h3
              data-flow-head={group.id}
              data-ui="register-flow-head"
            >
              {localName(group.category)}
              <span>
                {t(
                  countedKey(group.items.length, {
                    one: (m) => m.paperRegister.sectionDishesOne,
                    two: (m) => m.paperRegister.sectionDishesTwo,
                    few: (m) => m.paperRegister.sectionDishesFew,
                    many: (m) => m.paperRegister.sectionDishes,
                  }),
                  { count: group.items.length },
                )}
              </span>
            </h3>
            <div data-ui="dish-grid" className="grid shrink-0">
              {group.items.map(dishTile)}
            </div>
          </section>
        ))
      ) : null}
      {!flow && dishes !== null && dishes.length === 0 ? (
        <div data-ui="register-empty" className="text-center">
          {searching
            ? t((m) => m.paperRegister.noMatch, { query: query.trim() })
            : t((m) => m.paperRegister.sectionEmpty)}
        </div>
      ) : null}
      {flow ? null : (
      <div data-ui="dish-grid" className="grid shrink-0">
        {phone ? null : searching ? (
          <PaperBackTile
            sub={t((m) => m.paperRegister.clearSearch)}
            onClick={() => setQuery("")}
            testId="register-back-clear"
          />
        ) : section || other ? (
          <PaperBackTile
            sub={t((m) => m.paperRegister.backToSections)}
            onClick={() => setSection(null)}
            testId="register-back-sections"
          />
        ) : (
          <PaperBackTile
            sub={backLabel ?? t((m) => m.paperRegister.backToTables)}
            onClick={toTables}
            testId="register-back-tables"
          />
        )}
        {dishes !== null
          ? dishes.map(dishTile)
          : sections.map((category) => {
              const inSection = active.filter((item) =>
                item.categoryIds.includes(category.id),
              );
              return (
                <PaperSectionTile
                  key={category.id}
                  category={category}
                  name={localName(category)}
                  dishes={inSection.length}
                  inOrder={plates(inSection)}
                  onOpen={() => setSection(category.id)}
                />
              );
            })}
        {dishes === null && unfiled.length > 0 ? (
          <PaperSectionTile
            category={otherSection}
            name={t((m) => m.paperRegister.otherDishes)}
            dishes={unfiled.length}
            inOrder={plates(unfiled)}
            onOpen={() => setSection(OTHER_SECTION)}
          />
        ) : null}
      </div>
      )}
      {/* What a tile's `aria-describedby` reads: the hold has no visible chrome. */}
      <span id={holdHintId} hidden>
        {t((m) => m.paperRegister.holdHint)}
      </span>
      {sheet ? (
        <PaperDishActionSheet
          name={localName(sheet.item)}
          tile={sheet.tile}
          actions={actionsOf(sheet.item)}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </div>
  );
}

interface DishAction {
  key: "override" | "swap" | "availability";
  label: string;
  run: () => void;
}

/** How long a press must be held to open the sheet. */
const HOLD_MS = 450;
/** How far a finger may drift before the press counts as a scroll. */
const HOLD_SLOP_PX = 8;

/**
 * A dish tile that ADDS on a tap and opens its acts on a hold, a right-click
 * or the ContextMenu key / Shift+F10. A hold never also adds: the click that
 * follows the release is swallowed. Nothing here stops the grid scrolling —
 * a pan cancels the pointer, and the cancel clears the hold.
 */
function PaperDishButton({
  itemId,
  disabled,
  ariaDisabled,
  title,
  describedBy,
  soldOut,
  inOrder,
  onTap,
  onHold,
  children,
}: {
  itemId: string;
  disabled: boolean;
  ariaDisabled: boolean;
  title?: string;
  describedBy?: string;
  soldOut: boolean;
  inOrder: number;
  onTap: () => void;
  /** Absent when this session has no act to offer on this dish. */
  onHold?: (tile: HTMLElement) => void;
  children: ReactNode;
}) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  /** A hold opened the sheet: the click its release produces is not a tap. */
  const held = useRef(false);
  const clear = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => clear, []);

  const hold = (tile: HTMLElement) => {
    clear();
    held.current = true;
    onHold?.(tile);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={ariaDisabled || undefined}
      title={title}
      aria-describedby={describedBy}
      data-testid={`menu-item-${itemId}`}
      data-ui="dish-tile"
      data-kind="dish"
      data-sold-out={soldOut ? "" : undefined}
      data-in-order={inOrder > 0 ? "" : undefined}
      className="relative flex h-full w-full flex-col overflow-hidden text-start"
      onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => {
        held.current = false;
        if (!onHold || (event.button ?? 0) !== 0) return;
        start.current = { x: event.clientX, y: event.clientY };
        const tile = event.currentTarget;
        clear();
        timer.current = window.setTimeout(() => hold(tile), HOLD_MS);
      }}
      onPointerMove={(event: ReactPointerEvent<HTMLButtonElement>) => {
        if (timer.current === null || !start.current) return;
        if (
          Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y) >
          HOLD_SLOP_PX
        ) {
          clear();
        }
      }}
      onPointerUp={clear}
      onPointerCancel={clear}
      onPointerLeave={clear}
      onContextMenu={(event) => {
        if (!onHold) return;
        event.preventDefault();
        hold(event.currentTarget);
      }}
      onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Enter" || event.key === " ") held.current = false;
        if (onHold && (event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey))) {
          event.preventDefault();
          onHold(event.currentTarget);
        }
      }}
      onClick={() => {
        if (held.current) {
          held.current = false;
          return;
        }
        onTap();
      }}
    >
      {children}
    </button>
  );
}

/**
 * The sheet a held dish opens: the design's popover (white, 10px radius, the
 * three-layer shadow, 32px rows), anchored under the tile and kept on screen.
 * Each row closes the sheet and opens the act's own dialog or confirm. Esc, a
 * tap outside or a right-click outside closes it; focus goes back to the tile.
 */
function PaperDishActionSheet({
  name,
  tile,
  actions,
  onClose,
}: {
  name: string;
  tile: HTMLElement;
  actions: DishAction[];
  onClose: () => void;
}) {
  const t = useTranslate();
  const sheetRef = useRef<HTMLDivElement>(null);
  const close = () => {
    onClose();
    tile.focus();
  };
  useEffect(() => {
    sheetRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        tile.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, tile]);

  const rect = tile.getBoundingClientRect();
  const width = 220;
  const height = 44 + actions.length * 34;
  const rtl = document.documentElement.dir === "rtl";
  const below = rect.bottom - 8 + height <= window.innerHeight - 8;
  const top = below ? rect.bottom - 8 : Math.max(8, rect.top + 8 - height);
  const inline = Math.min(
    Math.max(8, (rtl ? window.innerWidth - rect.right : rect.left) + 8),
    Math.max(8, window.innerWidth - width - 8),
  );

  const move = (step: number) => {
    const items = [
      ...(sheetRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []),
    ];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(at + step + items.length) % items.length]?.focus();
  };

  return createPortal(
    <>
      <div
        aria-hidden="true"
        data-ui="dish-actions-backdrop"
        className="fixed inset-0 z-40"
        onClick={close}
        onContextMenu={(event) => {
          event.preventDefault();
          close();
        }}
      />
      <div
        ref={sheetRef}
        role="menu"
        aria-label={t((m) => m.paperRegister.dishActions, { name })}
        data-ui="dish-actions"
        data-testid="dish-actions"
        className="fixed z-50 flex flex-col"
        style={{ top, width, ...(rtl ? { right: inline } : { left: inline }) }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        <div data-ui="dish-actions-head" className="truncate">
          {name}
        </div>
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            data-ui="dish-action"
            data-testid={`dish-action-${action.key}`}
            className="flex items-center text-start"
            onClick={() => {
              onClose();
              action.run();
            }}
          >
            {action.label}
          </button>
        ))}
      </div>
    </>,
    document.body,
  );
}

/**
 * The Tables / Kitchen tabs over the PAPER register — the floor's own, with
 * Tables lit (the register is a step inside it, as in the design). Each one
 * goes back to the floor, on its own view. Counts and gates are the floor's:
 * busy tables, and the Kitchen tab only where `/kitchen` itself would show.
 *
 * B6 · P24b — on a bill at NO table (`place="offPremise"`) the register is a
 * step inside the Delivery & takeaway tab (the design lights `delivery` while
 * `table === 'Takeaway'`): that tab is drawn and lit, with the floor's count.
 */
export function PaperRegisterTabs({ place = "table" }: { place?: "table" | "offPremise" } = {}) {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale = "ar" } = useParams();
  const { data: session } = useSession();
  const canSee = hasPermission(session, PERMISSIONS.POS_USE);
  const kitchenTab = useFloorKitchenTab(canSee);
  const offPremise = place === "offPremise";
  const deliveryTab = useDeliveryTabBadge({
    enabled: canSee && offPremise,
    canClose: hasPermission(session, PERMISSIONS.INVOICE_CLOSE),
  });
  const grid = useApiQuery<TableGridArea[]>(queryKeys.grid, endpoints.restaurant.grid, {
    enabled: canSee && (kitchenTab.available || deliveryTab.available),
  });
  // Without the Kitchen tab the row is a lone "Tables" — the breadcrumb right
  // under it already goes back to the tables — so a waiter's phone gets that
  // height back for the menu instead (founder, 2026-10-06).
  if (!kitchenTab.available && !deliveryTab.available) return null;
  type RegisterView = "tables" | "kitchen" | "delivery";
  return (
    <PaperFloorTabs<RegisterView>
      value={offPremise && deliveryTab.available ? "delivery" : "tables"}
      onChange={(view) =>
        navigate(
          view === "kitchen"
            ? // The kitchen remembers this table, so its Tables tab comes back
              // here rather than to the list (`lib/tillReturn.ts`).
              `/${locale}/pos?view=kitchen&${TILL_RETURN_PARAM}=${encodeURIComponent(
                location.pathname + location.search,
              )}`
            : view === "delivery"
              ? `/${locale}/pos?view=delivery`
              : `/${locale}/pos`,
        )
      }
      tabs={[
        {
          value: "tables",
          label: t((m) => m.paperFloor.tabTables),
          icon: FLOOR_TAB_ICONS.tables,
          count: busyCount((grid.data ?? []).flatMap((area) => area.tables)),
          testId: "register-tab-tables",
        },
        ...(kitchenTab.available
          ? [
              {
                value: "kitchen" as const,
                label: t((m) => m.paperFloor.tabKitchen),
                icon: FLOOR_TAB_ICONS.kitchen,
                count: kitchenTab.count,
                tone: kitchenTab.waiting ? ("alert" as const) : ("info" as const),
                testId: "register-tab-kitchen",
              },
            ]
          : []),
        ...(deliveryTab.available
          ? [
              {
                value: "delivery" as const,
                label: t((m) => m.dispatch.tabDelivery),
                icon: FLOOR_TAB_ICONS.delivery,
                count: deliveryTab.count,
                tone: deliveryTab.tone,
                testId: "register-tab-delivery",
              },
            ]
          : []),
      ]}
    />
  );
}

/**
 * B6 · P24b — the register's crumbs on a bill at no table, the design's
 * `crumbs` for `fromDel`: «Delivery & takeaway / ⟨place⟩ / ⟨section⟩». The
 * first leads back to the Delivery tab; on the dishes step the place leads
 * back to the sections and the section is the current crumb (G24b L2). The
 * place is «New delivery|New pickup» until something is sent, then «Delivery
 * #n|Pickup #n». Only here: the dine-in register keeps the founder's
 * no-breadcrumb ruling (2026-10-07). A direct child of `till-menu`, so a phone
 * hides it with the other crumbs (`_paper-responsive.css`).
 */
export function PaperOffPremiseCrumbs({
  root,
  place,
  section,
  onRoot,
  onPlace,
}: {
  root: string;
  place: string;
  /** The open section's name on the dishes step, else null. */
  section: string | null;
  onRoot: () => void;
  onPlace: () => void;
}) {
  const crumbs: { label: string; onClick: (() => void) | null; testId: string }[] = [
    { label: root, onClick: onRoot, testId: "register-crumb-delivery" },
    { label: place, onClick: section ? onPlace : null, testId: "register-crumb-place" },
    ...(section ? [{ label: section, onClick: null, testId: "register-crumb-section" }] : []),
  ];
  return (
    <nav
      data-ui="register-crumbs"
      data-testid="register-crumbs"
      aria-label={root}
      className="flex shrink-0 flex-wrap items-center"
    >
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        return (
          <span key={crumb.testId} data-ui="register-crumb-item" className="flex items-center">
            {index > 0 ? (
              <span aria-hidden="true" data-ui="register-crumb-sep">
                /
              </span>
            ) : null}
            {crumb.onClick && !last ? (
              <button type="button" data-ui="register-crumb" data-testid={crumb.testId} onClick={crumb.onClick}>
                <bdi>{crumb.label}</bdi>
              </button>
            ) : (
              <span
                data-ui="register-crumb"
                data-current={last ? "" : undefined}
                aria-current={last ? "page" : undefined}
                data-testid={last ? "register-crumb-current" : crumb.testId}
              >
                <bdi>{crumb.label}</bdi>
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * The PAPER till's bar, on every till screen (floor, table, register): the
 * way out to the back office drawn as the design's sidebar glyph, then
 * "Store /" before the screen's title. The exit keeps its gate (`POS_USE`,
 * the back-office nav row's), its test id and its name.
 */
export function PaperTillBarLeading() {
  const t = useTranslate();
  const { locale = "ar" } = useParams();
  const { data: session } = useSession();
  // Only a role the back office is for (`modules/auth/backOffice.ts`): a
  // waiter's till has no way out to it.
  const canLeave = canUseBackOffice(session);
  const storeName = session?.seller.storeName ?? t((m) => m.common.appName);
  return (
    <span className="flex min-w-0 items-center gap-2">
      {canLeave ? (
        <Link
          to={`/${locale}/manage`}
          aria-label={t((m) => holdsAdministrationPermission(session?.seller.permissions) ? m.nav.manage : m.nav.cashierWorkspace)}
          title={t((m) => holdsAdministrationPermission(session?.seller.permissions) ? m.nav.manage : m.nav.cashierWorkspace)}
          className="flex shrink-0 items-center justify-center"
          data-testid="pos-exit-to-manage"
          data-ui="till-exit"
        >
          <PanelLeft aria-hidden="true" size={16} strokeWidth={1.75} />
        </Link>
      ) : null}
      <span data-ui="topbar-crumbs" className="flex min-w-0 items-center gap-[5px] text-[length:var(--font-size-sm)]">
        <span data-ui="topbar-crumb-store" className="truncate">
          {storeName}
        </span>
        <span aria-hidden="true" data-ui="topbar-crumb-sep">
          /
        </span>
      </span>
    </span>
  );
}
